import React, { useState, useEffect } from 'react';
import { Bell, X, Lightbulb, Megaphone, Send, ThumbsUp, ThumbsDown, CheckCircle, Clock, Layers } from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

export default function NotificationCenter({ sidebarCollapsed = true, collapsed = true, mobileOpen = false, inSidebar = false }) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('whats-new'); // 'whats-new' or 'ideas'
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewUpdate, setHasNewUpdate] = useState(false);
  
  // What's New state
  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(true);
  
  // Ideas state
  const [ideas, setIdeas] = useState([]);
  const [loadingIdeas, setLoadingIdeas] = useState(true);
  const [showIdeaForm, setShowIdeaForm] = useState(false);
  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    category: 'feature_request'
  });
  const [submittingIdea, setSubmittingIdea] = useState(false);

  // Fetch Platform Updates (What's New)
  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const { data, error } = await supabase
          .from('platform_updates')
          .select('*')
          .eq('is_published', true)
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) throw error;
        setUpdates(data || []);
        
        // Check for unread updates
        if (user?.id && data && data.length > 0) {
          const updateIds = data.map(u => u.id);
          
          const { data: viewedUpdates, error: viewError } = await supabase
            .from('user_update_views')
            .select('update_id')
            .eq('user_id', user.id)
            .in('update_id', updateIds);
          
          if (viewError) {
            console.error('Error fetching viewed updates:', viewError);
          } else {
            const viewedIds = new Set(viewedUpdates?.map(v => v.update_id) || []);
            const unreadUpdates = data.filter(u => !viewedIds.has(u.id));
            setUnreadCount(unreadUpdates.length);
            setHasNewUpdate(unreadUpdates.length > 0);
          }
        }
      } catch (error) {
        console.error('Error fetching updates:', error);
      } finally {
        setLoadingUpdates(false);
      }
    };

    if (isOpen && activeTab === 'whats-new') {
      fetchUpdates();
    } else if (!isOpen) {
      // Fetch unread count even when closed
      fetchUpdates();
    }
  }, [isOpen, activeTab, user?.id]);

  // Mark updates as read when viewing What's New tab
  useEffect(() => {
    const markAsRead = async () => {
      if (!isOpen || activeTab !== 'whats-new' || !user?.id || updates.length === 0) return;
      
      try {
        // Insert view records for all updates (UPSERT-like behavior)
        const viewRecords = updates.map(update => ({
          user_id: user.id,
          update_id: update.id
        }));
        
        const { error } = await supabase
          .from('user_update_views')
          .upsert(viewRecords, { 
            onConflict: 'user_id,update_id',
            ignoreDuplicates: true 
          });
        
        if (error) {
          console.error('Error marking updates as read:', error);
        } else {
          // Clear unread badge
          setUnreadCount(0);
          setHasNewUpdate(false);
        }
      } catch (error) {
        console.error('Error in markAsRead:', error);
      }
    };
    
    // Mark as read after a short delay (user has time to see the updates)
    const timer = setTimeout(markAsRead, 1000);
    return () => clearTimeout(timer);
  }, [isOpen, activeTab, user?.id, updates]);

  // Note: Real-time updates disabled - users see new updates on page refresh
  // This avoids WebSocket connection issues and is acceptable for platform updates

  // Fetch Feature Ideas
  useEffect(() => {
    const fetchIdeas = async () => {
      try {
        const { data, error } = await supabase
          .from('feature_ideas')
          .select('*, idea_votes(vote_type, user_id)')
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        // Calculate vote counts for each idea
        const ideasWithVotes = data?.map(idea => ({
          ...idea,
          upvote_count: idea.idea_votes?.filter(v => v.vote_type === 'upvote').length || 0,
          downvote_count: idea.idea_votes?.filter(v => v.vote_type === 'downvote').length || 0,
          user_vote: idea.idea_votes?.find(v => v.user_id === user?.id)?.vote_type || null
        })) || [];

        setIdeas(ideasWithVotes);
      } catch (error) {
        console.error('Error fetching ideas:', error);
      } finally {
        setLoadingIdeas(false);
      }
    };

    if (isOpen && activeTab === 'ideas') {
      fetchIdeas();
    }
  }, [isOpen, activeTab, user?.id]);

  // Submit new idea
  const handleSubmitIdea = async () => {
    if (!newIdea.title.trim() || !newIdea.description.trim()) return;

    setSubmittingIdea(true);
    try {
      const { error } = await supabase
        .from('feature_ideas')
        .insert([{
          title: newIdea.title.trim(),
          description: newIdea.description.trim(),
          category: newIdea.category,
          submitted_by: user?.id,
          submitted_by_email: user?.email,
          submitted_by_name: user?.email?.split('@')[0],
          tenant_id: user?.tenant_id
        }]);

      if (error) throw error;

      // Reset form and refresh ideas
      setNewIdea({ title: '', description: '', category: 'feature_request' });
      setShowIdeaForm(false);
      
      // Refresh ideas list
      const { data } = await supabase
        .from('feature_ideas')
        .select('*, idea_votes(vote_type, user_id)')
        .order('created_at', { ascending: false });
      
      const ideasWithVotes = data?.map(idea => ({
        ...idea,
        upvote_count: idea.idea_votes?.filter(v => v.vote_type === 'upvote').length || 0,
        downvote_count: idea.idea_votes?.filter(v => v.vote_type === 'downvote').length || 0,
        user_vote: idea.idea_votes?.find(v => v.user_id === user?.id)?.vote_type || null
      })) || [];
      
      setIdeas(ideasWithVotes);
    } catch (error) {
      console.error('Error submitting idea:', error);
      alert('Failed to submit idea. Please try again.');
    } finally {
      setSubmittingIdea(false);
    }
  };

  // Handle voting
  const handleVote = async (ideaId, voteType) => {
    try {
      const idea = ideas.find(i => i.id === ideaId);
      const existingVote = idea?.user_vote;

      if (existingVote === voteType) {
        // Remove vote
        await supabase
          .from('idea_votes')
          .delete()
          .eq('idea_id', ideaId)
          .eq('user_id', user?.id);
      } else if (existingVote) {
        // Update vote
        await supabase
          .from('idea_votes')
          .update({ vote_type: voteType })
          .eq('idea_id', ideaId)
          .eq('user_id', user?.id);
      } else {
        // Create new vote
        await supabase
          .from('idea_votes')
          .insert([{
            idea_id: ideaId,
            user_id: user?.id,
            vote_type: voteType
          }]);
      }

      // Refresh ideas
      const { data } = await supabase
        .from('feature_ideas')
        .select('*, idea_votes(vote_type, user_id)')
        .order('created_at', { ascending: false });
      
      const ideasWithVotes = data?.map(idea => ({
        ...idea,
        upvote_count: idea.idea_votes?.filter(v => v.vote_type === 'upvote').length || 0,
        downvote_count: idea.idea_votes?.filter(v => v.vote_type === 'downvote').length || 0,
        user_vote: idea.idea_votes?.find(v => v.user_id === user?.id)?.vote_type || null
      })) || [];
      
      setIdeas(ideasWithVotes);
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  // Status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      under_review: 'bg-yellow-100 text-yellow-800',
      planned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800'
    };
    
    const icons = {
      under_review: Clock,
      planned: Layers,
      in_progress: Clock,
      completed: CheckCircle
    };

    const Icon = icons[status] || Clock;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        <Icon className="w-3 h-3" />
        <span>{status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
      </span>
    );
  };

  return (
    <>
      {/* Bell Button - sidebar or floating */}
{inSidebar ? (
  <button
    onClick={() => setIsOpen(!isOpen)}
    className={`w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 text-slate-400 hover:bg-slate-800/50 hover:text-white relative`}
    aria-label="Open notification center"
  >
    <Bell className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} text-slate-500 group-hover:text-slate-300`} />
    {(!collapsed || mobileOpen) && (
      <span className="whitespace-nowrap overflow-hidden">What's New</span>
    )}
    {unreadCount > 0 && (
      <span className={`absolute ${(collapsed && !mobileOpen) ? 'top-1 right-1' : 'top-1 right-2'} min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1`}>
        {unreadCount > 9 ? '9+' : unreadCount}
      </span>
    )}
  </button>
) : (
  <button
    onClick={() => setIsOpen(!isOpen)}
    className={`fixed bottom-20 ${sidebarCollapsed ? 'left-20 lg:left-[88px]' : 'left-20 lg:left-[272px]'} z-[9999] w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group hover:scale-110 ${
      hasNewUpdate ? 'animate-pulse' : ''
    }`}
    aria-label="Open notification center"
  >
    <Bell className="w-5 h-5 text-white" />
    {unreadCount > 0 && (
      <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 animate-in zoom-in duration-200">
        {unreadCount > 9 ? '9+' : unreadCount}
      </span>
    )}
    <div className="absolute left-full ml-3 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
      {unreadCount > 0 ? `${unreadCount} new update${unreadCount > 1 ? 's' : ''}` : 'What\'s New & Ideas'}
    </div>
  </button>
)}

      {/* Popup Window */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div 
            className="fixed inset-0 bg-black/30 z-[9997] lg:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Popup */}
          <div className={`fixed ${inSidebar ? 'bottom-4 left-20 lg:left-[88px]' : `bottom-32 ${sidebarCollapsed ? 'left-20 lg:left-[88px]' : 'left-20 lg:left-[272px]'}`} z-[9998] w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col max-h-[600px]`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Surfox Updates</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('whats-new')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'whats-new'
                    ? 'text-indigo-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Megaphone className="w-4 h-4" />
                  <span>What's New</span>
                </div>
                {activeTab === 'whats-new' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
                )}
              </button>
              
              <button
                onClick={() => setActiveTab('ideas')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'ideas'
                    ? 'text-indigo-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Lightbulb className="w-4 h-4" />
                  <span>Ideas</span>
                </div>
                {activeTab === 'ideas' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
                )}
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* What's New Tab */}
              {activeTab === 'whats-new' && (
                <div className="p-4 space-y-4">
                  {loadingUpdates ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                    </div>
                  ) : updates.length === 0 ? (
                    <div className="text-center py-8">
                      <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No updates yet</p>
                      <p className="text-xs text-gray-400 mt-1">Check back soon for platform news!</p>
                    </div>
                  ) : (
                    updates.map((update) => (
                      <div key={update.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <h3 className="font-semibold text-gray-900 mb-2">{update.title}</h3>
                        <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">{update.content}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(update.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Ideas Tab */}
              {activeTab === 'ideas' && (
                <div className="p-4 space-y-4">
                  {/* Submit Idea Button/Form */}
                  {!showIdeaForm ? (
                    <button
                      onClick={() => setShowIdeaForm(true)}
                      className="w-full p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 font-medium"
                    >
                      <Send className="w-4 h-4" />
                      <span>Submit Your Idea</span>
                    </button>
                  ) : (
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg space-y-3 border border-indigo-100">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title *
                        </label>
                        <input
                          type="text"
                          value={newIdea.title}
                          onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                          placeholder="Brief title for your idea"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description *
                        </label>
                        <textarea
                          value={newIdea.description}
                          onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                          placeholder="Describe your idea in detail..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          value={newIdea.category}
                          onChange={(e) => setNewIdea({ ...newIdea, category: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        >
                          <option value="feature_request">Feature Request</option>
                          <option value="bug">Bug Report</option>
                          <option value="improvement">Improvement</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleSubmitIdea}
                          disabled={submittingIdea || !newIdea.title.trim() || !newIdea.description.trim()}
                          className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submittingIdea ? 'Submitting...' : 'Submit'}
                        </button>
                        <button
                          onClick={() => {
                            setShowIdeaForm(false);
                            setNewIdea({ title: '', description: '', category: 'feature_request' });
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Ideas List */}
                  {loadingIdeas ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                    </div>
                  ) : ideas.length === 0 ? (
                    <div className="text-center py-8">
                      <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No ideas yet</p>
                      <p className="text-xs text-gray-400 mt-1">Be the first to suggest an idea!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {ideas.map((idea) => (
                        <div key={idea.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-semibold text-gray-900 flex-1">{idea.title}</h3>
                            {getStatusBadge(idea.status)}
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-3">{idea.description}</p>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleVote(idea.id, 'upvote')}
                                className={`flex items-center space-x-1 px-2 py-1 rounded-lg transition-colors ${
                                  idea.user_vote === 'upvote'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                <ThumbsUp className="w-3 h-3" />
                                <span className="text-xs font-medium">{idea.upvote_count}</span>
                              </button>
                              
                              <button
                                onClick={() => handleVote(idea.id, 'downvote')}
                                className={`flex items-center space-x-1 px-2 py-1 rounded-lg transition-colors ${
                                  idea.user_vote === 'downvote'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                <ThumbsDown className="w-3 h-3" />
                                <span className="text-xs font-medium">{idea.downvote_count}</span>
                              </button>
                            </div>
                            
                            <span className="text-xs text-gray-400">
                              {new Date(idea.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}